import { differenceInDays } from 'date-fns';

export interface DepartmentResponse {
  date: string; // ISO date string: YYYY-MM-DD
  count: number;
}

export interface AssessmentProgressData {
  department_responses_by_date: DepartmentResponse[];
}

const toIsoDate = (d: Date | string): string => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const generateDateLabels = (start_date: Date | string, end_date: Date | string): string[] => {
  const parseDate = (d: Date | string) => {
    if (typeof d === 'string') {
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day);
    }
    const dateObj = new Date(d);
    // Use UTC getters to correctly interpret Date objects created from
    // 'YYYY-MM-DD' strings (which parse as UTC midnight in Node). This
    // preserves the intended calendar date when constructing local midnight.
    return new Date(
      dateObj.getUTCFullYear(),
      dateObj.getUTCMonth(),
      dateObj.getUTCDate()
    );
  };

  const normalizedStart = parseDate(start_date);
  const normalizedEnd = parseDate(end_date);

  const daysBetween = differenceInDays(normalizedEnd, normalizedStart) + 1;

  if (daysBetween <= 0) return [];

  return Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(normalizedStart);
    date.setDate(normalizedStart.getDate() + i);
    return toIsoDate(date);
  });
};

export const generateChartData = (
  assessment_progress_data: AssessmentProgressData,
  start_date: Date | string,
  end_date: Date | string
): number[] => {
  const responseDataMap = new Map<string, number>(
    assessment_progress_data.department_responses_by_date.map(item => [item.date, item.count])
  );

  const labels = generateDateLabels(start_date, end_date);

  return labels.map(label => responseDataMap.get(label) ?? 0);
};
